import { useEffect, useId } from "react";
import {
  type Control,
  Controller,
  type FieldValues,
  type Path,
  type UseFormTrigger,
  useWatch,
} from "react-hook-form";

import { InputText } from "@fielmann-ag/prisma-components";

import { PasswordRequirements } from "@/components/form/PasswordRequirements";
import { usePasswordMasking } from "@/components/form/usePasswordMasking";
import { useBlurFieldErrorVisibility } from "@/hooks/useBlurFieldErrorVisibility";
import { useI18n } from "@/i18n";
import { joinClassNames } from "@/lib/utils";
import { getVisibleError } from "@/utils/form/getVisibleError";
import {
  PASSWORD_POLICY_ERROR,
  validatePassword,
  validatePasswordRepeat,
} from "@/validation";

type PasswordCreationFormValues = FieldValues & {
  password: string;
  passwordRepeat: string;
};

interface PasswordCreationFieldsProps<
  TFieldValues extends PasswordCreationFormValues,
> {
  control: Control<TFieldValues>;
  trigger: UseFormTrigger<TFieldValues>;
  isSubmitted: boolean;
  dismissFormErrors: () => void;
  labels: {
    password: string;
    passwordRepeat: string;
  };
  /** Signup is side-by-side from `sm`; set-password stays stacked. */
  layout: "stacked" | "sideBySide";
  /** When set, password must not match this email (signup policy). */
  email?: string;
}

/**
 * New-password + repeat pair with live requirements.
 *
 * Shared by signup and set-password. Login's current-password field stays
 * screen-owned. The form must include `password` and `passwordRepeat`.
 */
export function PasswordCreationFields<
  TFieldValues extends PasswordCreationFormValues,
>({
  control,
  trigger,
  isSubmitted,
  dismissFormErrors,
  labels,
  layout,
  email,
}: PasswordCreationFieldsProps<TFieldValues>) {
  const { messages } = useI18n();
  const passwordField = "password" as Path<TFieldValues>;
  const passwordRepeatField = "passwordRepeat" as Path<TFieldValues>;
  const password = useWatch({ control, name: passwordField });
  const passwordRepeatErrorVisibility = useBlurFieldErrorVisibility();
  const passwordMasking = usePasswordMasking();
  const passwordRepeatMasking = usePasswordMasking();
  const passwordErrorId = useId();
  const passwordRequirementsId = useId();
  const passwordRepeatErrorId = useId();
  const fieldClassName =
    layout === "sideBySide" ? "min-w-0 w-full sm:flex-1" : "w-full";

  useEffect(() => {
    void trigger(passwordRepeatField);
  }, [password, passwordRepeatField, trigger]);

  useEffect(() => {
    if (email === undefined) {
      return;
    }

    void trigger(passwordField);
  }, [email, passwordField, trigger]);

  const fields = (
    <>
      {/* Password field */}
      <Controller
        control={control}
        name={passwordField}
        rules={{
          validate: (value) =>
            validatePassword(String(value ?? ""), {
              email,
              messages: messages.validation,
            }),
        }}
        render={({ field, fieldState }) => {
          const showError =
            fieldState.isTouched || fieldState.isDirty || isSubmitted;
          const error = getVisibleError(fieldState.error, showError);
          const inlineError =
            error && error !== PASSWORD_POLICY_ERROR ? error : undefined;

          return (
            <div className={joinClassNames(fieldClassName, "flex flex-col")}>
              <InputText
                label={labels.password}
                name={field.name}
                type={passwordMasking.inputType}
                value={String(field.value ?? "")}
                onChange={(value) => {
                  dismissFormErrors();
                  field.onChange(value);
                }}
                onBlur={field.onBlur}
                // Policy failures outline the field; their details stay in the
                // requirements list instead of the helper text.
                state={error ? "error" : undefined}
                isInvalid={Boolean(error)}
                aria-describedby={
                  inlineError
                    ? `${passwordErrorId} ${passwordRequirementsId}`
                    : passwordRequirementsId
                }
                autoComplete="new-password"
                isRequired
                helperText={
                  inlineError
                    ? {
                        state: "error",
                        label: inlineError,
                        id: passwordErrorId,
                      }
                    : undefined
                }
                className="w-full"
                inputActions={passwordMasking.inputActions}
              />
              <PasswordRequirements
                id={passwordRequirementsId}
                password={String(field.value ?? "")}
                showErrors={showError}
                className="mt-0.5 px-0.5"
              />
            </div>
          );
        }}
      />
      {/* Password confirmation field */}
      <Controller
        control={control}
        name={passwordRepeatField}
        rules={{
          validate: (value, formValues) =>
            validatePasswordRepeat(
              String(value ?? ""),
              String(formValues.password ?? ""),
              messages.validation,
            ),
        }}
        render={({ field, fieldState }) => {
          const error = getVisibleError(
            fieldState.error,
            passwordRepeatErrorVisibility.showError,
          );

          return (
            <div className={fieldClassName}>
              <InputText
                label={labels.passwordRepeat}
                name={field.name}
                type={passwordRepeatMasking.inputType}
                value={String(field.value ?? "")}
                onChange={(value) => {
                  dismissFormErrors();
                  passwordRepeatErrorVisibility.hideError();
                  field.onChange(value);
                }}
                onBlur={() => {
                  field.onBlur();
                  passwordRepeatErrorVisibility.revealError();
                }}
                state={error ? "error" : undefined}
                isInvalid={Boolean(error)}
                aria-describedby={error ? passwordRepeatErrorId : undefined}
                autoComplete="new-password"
                isRequired
                helperText={
                  error
                    ? {
                        state: "error",
                        label: error,
                        id: passwordRepeatErrorId,
                      }
                    : undefined
                }
                className="w-full"
                inputActions={passwordRepeatMasking.inputActions}
              />
            </div>
          );
        }}
      />
    </>
  );

  if (layout === "sideBySide") {
    return (
      <div
        data-testid="password-creation-fields"
        className="flex w-full flex-col gap-1 sm:flex-row"
      >
        {fields}
      </div>
    );
  }

  return (
    <div data-testid="password-creation-fields" className="contents">
      {fields}
    </div>
  );
}
